export interface NetworkPowerSignature {
  id?: string
  name?: string
  type?: string
}

export interface NetworkPowerPerson {
  total_count?: number
  inner_count?: number
  outer_count?: number
  strong_count?: number
  totalMembers?: unknown[]
  innerMembers?: unknown[]
  outerMembers?: unknown[]
  strongMembers?: unknown[]
  totalArrData?: NetworkPowerPerson[]
  strongArrData?: NetworkPowerPerson[]
  signatures?: NetworkPowerSignature[]
}

function numberValue(value: unknown): number {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

function arrayLength(value: unknown): number {
  return Array.isArray(value) ? value.length : 0
}

function clamp(value: number, min = 0, max = 1): number {
  return Math.max(min, Math.min(max, value))
}

function logScore(value: number, cap: number): number {
  if (value <= 0) return 0
  return clamp(Math.log(value + 1) / Math.log(cap + 1))
}

function totalConnectionCount(row: NetworkPowerPerson): number {
  const innerCount = Math.max(numberValue(row.inner_count), arrayLength(row.innerMembers))
  const outerCount = Math.max(numberValue(row.outer_count), arrayLength(row.outerMembers))

  return Math.max(
    numberValue(row.total_count),
    arrayLength(row.totalMembers),
    arrayLength(row.totalArrData),
    innerCount + outerCount,
  )
}

function strongConnectionCount(row: NetworkPowerPerson, totalCount: number): number {
  const strongCount = Math.max(
    numberValue(row.strong_count),
    arrayLength(row.strongMembers),
    arrayLength(row.strongArrData),
  )

  return Math.min(strongCount, totalCount)
}

function innerOuterCounts(row: NetworkPowerPerson, totalCount: number) {
  const innerCount = Math.min(
    Math.max(numberValue(row.inner_count), arrayLength(row.innerMembers)),
    totalCount,
  )
  const outerCount = Math.min(
    Math.max(numberValue(row.outer_count), arrayLength(row.outerMembers), totalCount - innerCount),
    Math.max(0, totalCount - innerCount),
  )

  return { innerCount, outerCount }
}

function visibleReachScore(row: NetworkPowerPerson, totalCount: number, strongCount: number): number {
  const standardCount = Math.max(0, totalCount - strongCount)
  const weightedDegree = (standardCount * 0.65) + (strongCount * 1.45)

  return logScore(weightedDegree, 28)
}

function strongRelationshipScore(totalCount: number, strongCount: number): number {
  if (totalCount <= 0 || strongCount <= 0) return 0

  const strongCountScore = logScore(strongCount, 10)
  const strongRatioScore = clamp(strongCount / totalCount)

  return (strongCountScore * 0.75) + (strongRatioScore * 0.25)
}

function spreadScore(row: NetworkPowerPerson, totalCount: number): number {
  if (totalCount <= 0) return 0

  const { innerCount, outerCount } = innerOuterCounts(row, totalCount)
  const bridgeReach = logScore(outerCount, 10)
  const teamIntegration = logScore(innerCount, 10)
  const balance = 1 - (Math.abs(innerCount - outerCount) / totalCount)

  return clamp((bridgeReach * 0.45) + (teamIntegration * 0.25) + (balance * 0.3))
}

function neighborReachScore(row: NetworkPowerPerson, totalCount: number): number {
  const neighbors = Array.isArray(row.totalArrData) ? row.totalArrData : []
  const neighborCount = Math.max(neighbors.length, arrayLength(row.totalMembers))

  if (neighbors.length === 0) {
    return logScore(totalCount, 28) * 0.35
  }

  const topNeighborScores = neighbors
    .map(neighbor => logScore(totalConnectionCount(neighbor), 28))
    .sort((a, b) => b - a)
    .slice(0, 6)

  const topNeighborAverage = topNeighborScores.length > 0
    ? topNeighborScores.reduce((sum, score) => sum + score, 0) / topNeighborScores.length
    : 0
  const breadth = logScore(neighborCount, 16)

  return clamp((topNeighborAverage * 0.7) + (breadth * 0.3))
}

function signatureRoleScore(row: NetworkPowerPerson): number {
  if (!Array.isArray(row.signatures) || row.signatures.length === 0) return 0

  const weights: Record<string, number> = {
    hub: 1,
    influence: 0.92,
    collective: 0.88,
    connectivity: 0.82,
    brokerage: 0.78,
    strategist: 0.74,
    reachability: 0.66,
    'community leader': 0.64,
    linker: 0.54,
    cohesion: 0.46,
    ripple: 0.44,
    insular: -0.35,
    peripheral: -0.45,
    distant: -0.45,
  }

  const positiveScores: number[] = []
  let penalty = 0

  row.signatures.forEach(signature => {
    const key = String(signature.name || signature.type || signature.id || '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim()
      .toLowerCase()
    const score = weights[key] || 0

    if (score > 0) {
      positiveScores.push(score)
    } else {
      penalty += score
    }
  })

  positiveScores.sort((a, b) => b - a)

  const primaryRole = positiveScores[0] || 0
  const supportingRoles = positiveScores
    .slice(1, 3)
    .reduce((sum, score) => sum + (score * 0.12), 0)

  return clamp(primaryRole + supportingRoles + penalty)
}

export function calculateNetworkPower(row: NetworkPowerPerson | null | undefined): number {
  if (!row || typeof row !== 'object') return 0

  const totalCount = totalConnectionCount(row)
  if (totalCount <= 0) return 0

  const strongCount = strongConnectionCount(row, totalCount)
  const score =
    (visibleReachScore(row, totalCount, strongCount) * 45) +
    (strongRelationshipScore(totalCount, strongCount) * 25) +
    (spreadScore(row, totalCount) * 15) +
    (neighborReachScore(row, totalCount) * 10) +
    (signatureRoleScore(row) * 5)

  return Math.round(clamp(score, 0, 100))
}
